import { OnInit, OnDestroy, ChangeDetectorRef, EventEmitter } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatPaginator } from '@angular/material/paginator';
import { Subject } from 'rxjs';
import { takeUntil, skip, take, distinctUntilChanged, debounceTime, map } from 'rxjs/operators';
import { Store } from '@ngrx/store';
import { Selectors, Actions } from 'store/root-reducer';
import { CustomerActionTypes } from 'store/actions/customer.actions';
import { UserNotificationDataSource } from 'modules/shared/data-source/user-notifications.data-source';
import { SnackbarService } from 'app/services/snackbar.service';
var UserNotificationsComponent = /** @class */ (function () {
    function UserNotificationsComponent(snackbar, store, cdRef) {
        this.snackbar = snackbar;
        this.store = store;
        this.cdRef = cdRef;
        this.showActionButtons = true;
        this.showResetButton = true;
        this.showPopover = true;
        this.takeDefaultUsers = true;
        this.submitUpdatedNotificationList = new EventEmitter();
        this.defaultUsersSaved = new EventEmitter();
        this.payload = {};
        this.unsubscribe$ = new Subject();
        this.pageSizeOptions = [5, 10, 15];
        this._allDomains = [];
        this.displayedColumns = ['checked', 'user', 'l1', 'l2', 'l3'];
        this.filter = {
            title: 'ROLES',
            items: ['L1', 'L2', 'L3'].map(function (roleValue) { return ({ subType: 'ROLE', key: roleValue }); })
        };
    }
    Object.defineProperty(UserNotificationsComponent.prototype, "newDomain", {
        set: function (domain) {
            if (domain) {
                this.selectedDomain = Object.assign({}, domain);
                this.dataSource.setCurrentDomain(domain);
                this.payload.domainObject = this.selectedDomain;
                this.dataSource.loadUserData(this.payload);
            }
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(UserNotificationsComponent.prototype, "allDomains", {
        set: function (domains) {
            this._allDomains = domains.slice();
        },
        enumerable: true,
        configurable: true
    });
    UserNotificationsComponent.prototype.ngOnInit = function () {
        this.getLoadingStatus();
        this.searchFormControl = new FormControl('');
        this.getCurrentDomain();
        this.initSearchSubscription();
        this.checkErrors();
    };
    UserNotificationsComponent.prototype.ngOnDestroy = function () {
        this.unsubscribe$.next();
        this.unsubscribe$.complete();
    };
    /**
     * Get loading status from store
     * @method getLoadingStatus
     */
    UserNotificationsComponent.prototype.getLoadingStatus = function () {
        this.loading$ = this.store.select(Selectors.customer.getLoading)
            .pipe(takeUntil(this.unsubscribe$), map(function (customerLoading) {
            return customerLoading.includes(CustomerActionTypes.GET_USERS);
        }));
    };
    UserNotificationsComponent.prototype.getCurrentDomain = function () {
        var _this = this;
        this.store
            .select(Selectors.domain.getCurrentDomain)
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe(function (domain) {
            _this.selectedDomain = domain;
            _this.payload.domainObject = _this.selectedDomain;
            _this.setDataSource();
            _this.dataSource.loadUserData(_this.payload);
        });
    };
    UserNotificationsComponent.prototype.initSearchSubscription = function () {
        var _this = this;
        this.searchFormControl
            .valueChanges
            .pipe(distinctUntilChanged(), debounceTime(200))
            .subscribe(function (value) {
            _this.paginator.pageIndex = 0;
            _this.payload.searchName = value;
            _this.dataSource.loadUserData(_this.payload);
        });
    };
    /**
     * Prepares data source
     * @method setDataSource
     */
    UserNotificationsComponent.prototype.setDataSource = function () {
        this.dataSource = new UserNotificationDataSource(this.store, this.takeDefaultUsers, this.selectedDomain);
        this.dataSource.paginator = this.paginator;
        this.dataSource.paginator.pageSizeOptions = this.pageSizeOptions;
    };
    /**
     * Check errors in the store for relevant
     * @method checkErrors
     */
    UserNotificationsComponent.prototype.checkErrors = function () {
        var _this = this;
        this.store
            .select(Selectors.customer.getErrors)
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe(function (errors) {
            if (errors.some(function (error) { return error.action === CustomerActionTypes.SET_DEFAULT_NOTIFIED_USERS; })) {
                _this.snackbar.error('Failed to set default notified users, please try again later.');
                _this.dataSource.loadUserData();
            }
            _this.cdRef.detectChanges();
        });
    };
    UserNotificationsComponent.prototype.onSelectionChangeHandler = function (data) {
        this.payload.roles = data.value.map(function (selectedItem) { return selectedItem.key; }).join(',');
        this.paginator.pageIndex = 0;
        this.dataSource.loadUserData(this.payload);
    };
    UserNotificationsComponent.prototype.onDomainFilterHandler = function ($event) {
        var selectedDomainId = this._allDomains.findIndex(function (domain) { return domain.id === $event.value; });
        if (selectedDomainId >= 0) {
            this.selectedDomain = this._allDomains[selectedDomainId];
            this.payload.domainObject = this.selectedDomain;
            this.store.dispatch(new Actions.customer.GetDefaultNotifiedUsers({
                domain: this.selectedDomain
            }));
        }
        this.dataSource.paginator.pageIndex = 0;
        this.dataSource.loadUserData(this.payload);
        this.cdRef.detectChanges();
    };
    /**
     * Clear default users
     * @method clearDefaultUsers
     */
    UserNotificationsComponent.prototype.clearDefaultUsers = function () {
        var _this = this;
        this.dataSource.clearAllSelected();
        var domainSelectedUsersPayload = {
            domainId: this.selectedDomain.id,
            users: []
        };
        this.store.select(Selectors.customer.getDefaultNotifiedUsers)
            .pipe(skip(1), take(1))
            .subscribe(function () {
            _this.snackbar.success('Preferences successfully removed.' +
                ' You will need to select users to notify each time you onboard a customer.');
        });
        this.store.dispatch(new Actions.customer.SetDefaultNotifiedUsers(domainSelectedUsersPayload));
        this.cdRef.detectChanges();
    };
    UserNotificationsComponent.prototype.updateAllNotifications = function (user, event) {
        this.dataSource.selectItem(user, event.checked);
        this.cdRef.detectChanges();
    };
    UserNotificationsComponent.prototype.saveSelectedUsers = function () {
        var domainSelectedUsersPayload = {
            domainId: this.selectedDomain.id,
            users: this.dataSource.getSelectedUsers()
        };
        var isSelectedUserValid = this.dataSource.getSelectedUsers().some(function (selectedUser) { return selectedUser.l1; }) &&
            this.dataSource.getSelectedUsers().some(function (selectedUser) { return selectedUser.l2; }) &&
            this.dataSource.getSelectedUsers().some(function (selectedUser) { return selectedUser.l3; });
        if (!isSelectedUserValid) {
            this.snackbar.error('Select at least one L1 user, one L2 user and one L3 user before saving.');
        }
        else {
            this.store.dispatch(new Actions.customer.SetDefaultNotifiedUsers(domainSelectedUsersPayload));
            this.defaultUsersSaved.emit(true);
            this.snackbar.success('Preferences successfully updated.');
        }
    };
    /**
     * Submit selected users to update access control for a certain customer
     * @method submitSelected
     */
    UserNotificationsComponent.prototype.submitSelected = function () {
        var isSelectedUserValid = this.dataSource.getSelectedUsers().some(function (selectedUser) { return selectedUser.l1; }) &&
            this.dataSource.getSelectedUsers().some(function (selectedUser) { return selectedUser.l2; }) &&
            this.dataSource.getSelectedUsers().some(function (selectedUser) { return selectedUser.l3; });
        if (isSelectedUserValid) {
            this.submitUpdatedNotificationList.emit(this.dataSource.getSelectedUsers());
        }
        else {
            this.snackbar.error('Select at least one L1 user, one L2 user and one L3 user before saving.');
        }
    };
    return UserNotificationsComponent;
}());
export { UserNotificationsComponent };
